import { CheckIcon, RepeatIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  CheckboxProps,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea,
  TextareaProps,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useDebounceCallback } from "@react-hook/debounce";
import {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Item, Menu, Submenu, useContextMenu } from "react-contexify";
import { SpotType } from "~/generated/graphql.types";
import { EditModeContext } from "../..";
import {
  useGetSpotQuery,
  useGetTextToSpeechAudioQuery,
  useUpdateSpotMutation,
} from "./index.generated";
import { AudioPlayer } from "~/routes/_shared/AudioPlayer";

const StyledTextarea = ({
  maxCount,
  ...props
}: TextareaProps & { maxCount?: number }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textCount = useMemo(() => {
    return (
      textareaRef.current?.value.length ||
      textareaRef.current?.defaultValue.length ||
      0
    );
  }, [textareaRef.current?.value, textareaRef.current?.defaultValue]);
  return (
    <>
      <Textarea
        ref={textareaRef}
        resize="none"
        color="white"
        rounded="none"
        bgColor="#071323"
        border="none"
        p={2}
        overflowY={"scroll"}
        sx={{
          "&::-webkit-scrollbar": {
            width: 2,
            backgroundColor: "pink.700",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: `blue.400`,
          },
        }}
        {...props}
      />
      {maxCount !== undefined ? (
        <Flex justifyContent="flex-end">
          <Text color={textCount <= maxCount ? "white" : "red.500"}>
            {textCount} / {maxCount}
          </Text>
        </Flex>
      ) : null}
    </>
  );
};

const StyledCheckbox = (props: CheckboxProps) => {
  return (
    <Checkbox size="lg" p={2} bgColor="pink.700" {...props}>
      {props.children}
    </Checkbox>
  );
};

const NarrationTextForTtsEditor = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectionStart, setSelectionStart] = useState<number>(0);
  const [selectionEnd, setSelectionEnd] = useState<number>(0);

  useEffect(() => {
    if (isEditing) {
      textareaRef.current?.focus();
    }
  }, [isEditing]);

  const { show: showContextMenu } = useContextMenu({
    id: "narraion-text-for-tts-editor-context-menu",
  });

  return isEditing ? (
    <>
      <Menu id="narraion-text-for-tts-editor-context-menu">
        <Submenu label="SBR:0 (読みタイミング調整)">
          {[...Array(6)].map((_, i) => (
            <Item
              key={i}
              onMouseDown={() =>
                onChange(
                  value.slice(0, selectionStart) +
                    " {SBR:0} ".repeat(i + 1) +
                    value.slice(selectionEnd)
                )
              }
            >
              {i + 1}個
            </Item>
          ))}
        </Submenu>
        <Item
          onMouseDown={() =>
            onChange(
              value.slice(0, selectionStart) +
                " {SBR:Buddy} " +
                value.slice(selectionEnd)
            )
          }
        >
          SBR:Buddy
        </Item>
        <Item
          onMouseDown={() =>
            onChange(
              value.slice(0, selectionStart) +
                " {SBR:NEXT} " +
                value.slice(selectionEnd)
            )
          }
        >
          SBR:NEXT
        </Item>
        <Item
          onMouseDown={() =>
            onChange(
              value.slice(0, selectionStart) +
                " {SBR:PREV} " +
                value.slice(selectionEnd)
            )
          }
        >
          SBR:PREV
        </Item>
      </Menu>
      <Textarea
        ref={textareaRef}
        resize="none"
        color="white"
        rounded="none"
        bgColor="#071323"
        border="none"
        p={2}
        flex={1}
        overflowY={"scroll"}
        sx={{
          "&::-webkit-scrollbar": {
            width: 2,
            backgroundColor: "pink.700",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: `blue.400`,
          },
        }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onContextMenu={(e) => showContextMenu({ event: e })}
        onSelect={(e) => {
          setSelectionStart(e.currentTarget.selectionStart);
          setSelectionEnd(e.currentTarget.selectionEnd);
        }}
      />
      <Button
        aria-label="ok"
        onClick={() => setIsEditing(false)}
        rounded="none"
        colorScheme="pink"
      >
        <CheckIcon />
      </Button>
    </>
  ) : (
    <Box
      resize="none"
      color="white"
      rounded="none"
      bgColor="#071323"
      border="none"
      h="100%"
      overflowY={"scroll"}
      sx={{
        "&::-webkit-scrollbar": {
          width: 2,
          backgroundColor: "pink.700",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: `blue.400`,
        },
      }}
      p={2}
      onClick={() => setIsEditing(true)}
    >
      {value
        .replaceAll("　", " ")
        .split(" ")
        .map((token, i) => {
          if (/^\{SBR\:.+\}( |\n|$)/.test(token)) {
            return (
              <Box
                key={i}
                as="span"
                color="yellow.500"
                wordBreak="break-word"
                fontWeight={700}
              >
                {` ${token.replace(/[\{\}]/g, "")} `}
              </Box>
            );
          } else {
            return (
              <Box w="full" wordBreak="break-word" as="span" key={i}>
                {token}
              </Box>
            );
          }
        })}
    </Box>
  );
};

const ContentsForm = ({
  id,
  spotType,
  defaultValues,
}: {
  id: string;
  spotType: SpotType;
  defaultValues: {
    name?: string | null;
    description?: string | null;
    narrationText?: string | null;
    narrationTextForTtsBeforeMusic?: string | null;
    musicQuestion?: string | null;
    hasRestaurant: boolean;
    hasRestroom: boolean;
    hasCafe: boolean;
    hasFreeParking: boolean;
    hasPaidParking: boolean;
    hasEvCharger: boolean;
  };
}) => {
  const [name, setName] = useState<string>(defaultValues.name || "");
  const [description, setDescription] = useState<string>(
    defaultValues.description || ""
  );
  const [narrationText, setNarrationText] = useState<string>(
    defaultValues.narrationText || ""
  );
  const [narrationTextForTtsBeforeMusic, setNarrationTextForTtsBeforeMusic] =
    useState<string>(defaultValues.narrationTextForTtsBeforeMusic || "");
  const [musicQuestion, setMusicQuestion] = useState<string>(
    defaultValues.musicQuestion || ""
  );
  const [hasRestaurant, setHasRestaurant] = useState<boolean>(
    defaultValues.hasRestaurant
  );
  const [hasRestroom, setHasRestroom] = useState<boolean>(
    defaultValues.hasRestroom
  );
  const [hasCafe, setHasCafe] = useState<boolean>(defaultValues.hasCafe);
  const [hasFreeParking, setHasFreeParking] = useState<boolean>(
    defaultValues.hasFreeParking
  );
  const [hasPaidParking, setHasPaidParking] = useState<boolean>(
    defaultValues.hasPaidParking
  );
  const [hasEvCharger, setHasEvCharger] = useState<boolean>(
    defaultValues.hasEvCharger
  );

  const [displayMusicQuestionInput, setDisplayMusicQuestionInput] =
    useState<boolean>(!!defaultValues.musicQuestion);
  const [updateSpotRes, updateSpot] = useUpdateSpotMutation();
  const updateSpotWidthDebounce = useDebounceCallback(updateSpot, 500);

  const [ttsTextCache, setTTSTextCache] = useState<string | undefined>();
  const ttsTextCacheOutDated = ttsTextCache != narrationTextForTtsBeforeMusic;
  const [ttsAudioResp, _getTTSAudio] = useGetTextToSpeechAudioQuery({
    pause: true,
    variables: {
      narration: narrationTextForTtsBeforeMusic ?? "",
    },
    requestPolicy: "network-only",
  });
  const featureFlagElevenlabs =
    import.meta.env.VITE_FEATURE_FLAG_USE_ELEVENLABS_VOICE === "true";

  return (
    <HStack w="full" p={3} h="full">
      <VStack display="flex" flexDir="column" flex={1} h="full">
        <FormControl display="flex" flexDir="column" flex={1}>
          <FormLabel color="white">スポット名</FormLabel>
          <StyledTextarea
            flex={1}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              updateSpotWidthDebounce({
                input: {
                  id,
                  name: e.target.value,
                },
              });
            }}
            maxCount={30}
          />
        </FormControl>
        {spotType !== SpotType.Narration ? (
          <FormControl display="flex" flexDir="column" flex={1}>
            <FormLabel color="white">ディスクリプション</FormLabel>
            <StyledTextarea
              flex={1}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                updateSpotWidthDebounce({
                  input: {
                    id,
                    description: e.target.value,
                  },
                });
              }}
              maxCount={80}
            />
          </FormControl>
        ) : (
          <Box flex={1}></Box>
        )}
      </VStack>

      <FormControl display="flex" flexDir="column" flex={1} h="full">
        <FormLabel color="white">音声ガイド（原文）</FormLabel>
        <StyledTextarea
          flex={1}
          value={narrationText}
          onChange={(e) => {
            setNarrationText(e.target.value);
            updateSpotWidthDebounce({
              input: {
                id,
                narrationText: e.target.value,
              },
            });
          }}
        />
      </FormControl>

      <FormControl display="flex" flexDir="column" flex={1} h="full">
        <FormLabel>
          <Flex
            flexDir="row"
            alignItems="center"
            justifyContent="space-between"
            color="white"
          >
            <Text>音声ガイド（読み調整後）</Text>
            {ttsAudioResp.fetching && <Spinner />}
            {!ttsAudioResp.fetching &&
              (ttsTextCacheOutDated ||
                !ttsAudioResp.data ||
                featureFlagElevenlabs) && (
                <IconButton
                  icon={<RepeatIcon />}
                  aria-label="play"
                  variant="outline"
                  verticalAlign="middle"
                  textColor={"white"}
                  size="sm"
                  onClick={() => {
                    setTTSTextCache(narrationTextForTtsBeforeMusic);
                    if (
                      narrationTextForTtsBeforeMusic != undefined ||
                      narrationTextForTtsBeforeMusic != "" ||
                      featureFlagElevenlabs
                    )
                      _getTTSAudio();
                  }}
                  disabled={
                    (narrationTextForTtsBeforeMusic === undefined ||
                      narrationTextForTtsBeforeMusic === "") &&
                    !featureFlagElevenlabs
                  }
                />
              )}
            {/* コンポーネントを複数作ると過去のコンポーネントのaudioを止めることができないため、
            一つのコンポーネントを使い回す。そして、activatedで表示を切り替える。
            */}
            <AudioPlayer
              encodedAudioData={
                ttsAudioResp.data?.textToSpeechEncodedAudio.encodedAudio || ""
              }
              activated={
                !ttsAudioResp.fetching &&
                !ttsTextCacheOutDated &&
                ttsAudioResp.data !== undefined
              }
            />
          </Flex>
        </FormLabel>
        <NarrationTextForTtsEditor
          value={narrationTextForTtsBeforeMusic}
          onChange={(value) => {
            setNarrationTextForTtsBeforeMusic(value);
            updateSpotWidthDebounce({
              input: {
                id,
                narrationTextForTtsBeforeMusic: value,
              },
            });
          }}
        />
      </FormControl>

      <VStack flex={0.6} color="white" h="full">
        {[SpotType.Narration, SpotType.SubaruMoment].includes(spotType) ? (
          <VStack w="full" alignItems="flex-start">
            <FormLabel w="full">曲再生</FormLabel>
            <RadioGroup
              w="full"
              value={displayMusicQuestionInput ? "has_music" : "no_music"}
              onChange={(value) => {
                if (value === "has_music") {
                  setDisplayMusicQuestionInput(true);
                  updateSpotWidthDebounce({
                    input: {
                      id,
                      musicQuestion: musicQuestion,
                    },
                  });
                } else {
                  setDisplayMusicQuestionInput(false);
                  updateSpotWidthDebounce({
                    input: {
                      id,
                      musicQuestion: "",
                    },
                  });
                }
              }}
            >
              <Stack spacing={2}>
                <Box bgColor="pink.700" p={2}>
                  <Radio value="no_music">なし</Radio>
                </Box>
                <Box bgColor="pink.700" p={2}>
                  <Radio value="has_music">あり</Radio>
                </Box>
              </Stack>
            </RadioGroup>

            {displayMusicQuestionInput ? (
              <>
                <FormLabel color="white">曲の質問</FormLabel>
                <StyledTextarea
                  flex={1}
                  value={musicQuestion}
                  onChange={(e) => {
                    setMusicQuestion(e.target.value);
                    updateSpotWidthDebounce({
                      input: {
                        id,
                        musicQuestion: e.target.value,
                      },
                    });
                  }}
                />
              </>
            ) : null}
          </VStack>
        ) : (
          <VStack w="full">
            <FormLabel w="full">施設</FormLabel>
            <Stack w="full" spacing={2}>
              <StyledCheckbox
                isChecked={hasRestaurant}
                onChange={(e) => {
                  setHasRestaurant(e.target.checked);
                  updateSpot({
                    input: {
                      id,
                      hasRestaurant: e.target.checked,
                    },
                  });
                }}
              >
                <Text fontSize="sm">レストラン</Text>
              </StyledCheckbox>
              <StyledCheckbox
                isChecked={hasCafe}
                onChange={(e) => {
                  setHasCafe(e.target.checked);
                  updateSpot({
                    input: {
                      id,
                      hasCafe: e.target.checked,
                    },
                  });
                }}
              >
                <Text fontSize="sm">カフェ・軽食</Text>
              </StyledCheckbox>
              <StyledCheckbox
                isChecked={hasRestroom}
                onChange={(e) => {
                  setHasRestroom(e.target.checked);
                  updateSpot({
                    input: {
                      id,
                      hasRestroom: e.target.checked,
                    },
                  });
                }}
              >
                <Text fontSize="sm">トイレ</Text>
              </StyledCheckbox>
              <StyledCheckbox
                isChecked={hasPaidParking}
                onChange={(e) => {
                  setHasPaidParking(e.target.checked);
                  updateSpot({
                    input: {
                      id,
                      hasPaidParking: e.target.checked,
                    },
                  });
                }}
              >
                <Text fontSize="sm">有料駐車場</Text>
              </StyledCheckbox>
              <StyledCheckbox
                isChecked={hasFreeParking}
                onChange={(e) => {
                  setHasFreeParking(e.target.checked);
                  updateSpot({
                    input: {
                      id,
                      hasFreeParking: e.target.checked,
                    },
                  });
                }}
              >
                <Text fontSize="sm">無料駐車場</Text>
              </StyledCheckbox>
              <StyledCheckbox
                isChecked={hasEvCharger}
                onChange={(e) => {
                  setHasEvCharger(e.target.checked);
                  updateSpot({
                    input: {
                      id,
                      hasEvCharger: e.target.checked,
                    },
                  });
                }}
              >
                <Text fontSize="sm">EV</Text>
              </StyledCheckbox>
            </Stack>
          </VStack>
        )}
        {updateSpotRes.fetching ? <Spinner colorScheme="blue" /> : null}
      </VStack>
    </HStack>
  );
};

const Inner = () => {
  const { spotIdForEdit } = useContext(EditModeContext);
  const [getSpotRes] = useGetSpotQuery({
    variables: {
      id: spotIdForEdit!,
    },
    pause: !spotIdForEdit,
  });
  const spot = getSpotRes.data?.spot!;

  const FormForSpecifiedSpot = useCallback(() => {
    return (
      <ContentsForm
        id={spot.id}
        spotType={spot.spotType}
        defaultValues={{
          name: spot.name,
          description: spot.description,
          narrationText: spot.narrationText,
          narrationTextForTtsBeforeMusic: spot.narrationTextForTtsBeforeMusic,
          musicQuestion: spot.musicQuestion,
          hasRestaurant: spot.hasRestaurant,
          hasCafe: spot.hasCafe,
          hasRestroom: spot.hasRestroom,
          hasPaidParking: spot.hasPaidParking,
          hasFreeParking: spot.hasFreeParking,
          hasEvCharger: spot.hasEvCharger,
        }}
      />
    );
  }, [spotIdForEdit]);

  return spot && spotIdForEdit !== undefined ? (
    spot.spotType !== SpotType.RoutingGuide ? (
      <FormForSpecifiedSpot />
    ) : (
      <Box w="full" h="full" m={16}>
        <Text fontSize="xl" color="white" textAlign="center" fontWeight={600}>
          ルート誘導用スポットは編集できません
        </Text>
      </Box>
    )
  ) : (
    <Box w="full" h="full" m={16}>
      <Text fontSize="xl" color="white" textAlign="center" fontWeight={600}>
        スポットを選択してください
      </Text>
    </Box>
  );
};

export const ContentsEditor = () => {
  return (
    <Flex
      bottom={0}
      position="fixed"
      bgColor="pink.900"
      w="calc(100vw - 500px)"
      h={400}
      flexDir="row"
    >
      <Suspense
        fallback={
          <Box w="full" h="full" m={16}>
            <Spinner />
          </Box>
        }
      >
        <Inner />
      </Suspense>
    </Flex>
  );
};
